import { Fragment, useCallback, useEffect, useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { UserManagementPanel, RoleManagementPanel } from 'auth-sso-kit/client/index.js';
import { AuditLogViewer } from 'audit-log-kit/client/index.js';
import { NotificationPreferences } from 'notification-kit/client/index.js';
import { api } from '../api/client';
import type { AdminStats, AtelierCoanimation, EtatExportExcel, ModeCoanimation, OutboxMessage, RetourAvis, StatutAvis, StatutMessage } from '../types';
import { JOURS_SEMAINE, formatHoraire } from '../components/annuaire/format';
import { EXPLICATION_MODE_COANIMATION, LIBELLE_MODE_COANIMATION } from '../components/demande/coanimation';
import { CLASSE_BADGE_ETAT, LIBELLE_CANAL, LIBELLE_ETAT, ligneEtat, dePersonne } from '../components/messages/format';
import { annoncerMiseAJourMessages } from '../components/messages/useCompteMessages';

type Onglet = 'utilisateurs' | 'roles' | 'reglages' | 'coanimation' | 'outbox' | 'eveil' | 'audit' | 'notifications' | 'retours';

const ONGLETS: { id: Onglet; label: string }[] = [
  { id: 'utilisateurs', label: 'Utilisateurs' },
  { id: 'roles', label: 'Rôles' },
  { id: 'reglages', label: 'Réglages' },
  { id: 'coanimation', label: 'Co-animation' },
  { id: 'outbox', label: 'Historique des messages' },
  { id: 'eveil', label: 'Eveil V2.1' },
  { id: 'audit', label: "Journal d'audit" },
  { id: 'notifications', label: 'Notifications' },
  { id: 'retours', label: 'Retours' },
];

export function AdminPage({ params = {} }: { params?: Record<string, string> }) {
  // Un lien « #admin?onglet=retours » (la cloche) ouvre directement l'onglet.
  const [onglet, setOnglet] = useState<Onglet>(() => ONGLETS.find((o) => o.id === params.onglet)?.id ?? 'utilisateurs');
  const [sante, setSante] = useState<AdminStats | null>(null);

  useEffect(() => {
    api.getAdminStats().then(setSante).catch(() => {});
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading"><span className="page-title">Administration</span></div>
      </div>

      {sante && (
        <div className="flex gap-16 mb-16" style={{ fontSize: 'var(--t-13)', color: 'var(--ink-3)' }}>
          <span>{sante.utilisateurs} utilisateur{sante.utilisateurs !== 1 ? 's' : ''}</span>
          <span style={sante.outbox_en_attente > 0 ? { color: 'var(--warn)' } : undefined}>
            {sante.outbox_en_attente} message{sante.outbox_en_attente !== 1 ? 's' : ''} pas encore envoyé{sante.outbox_en_attente !== 1 ? 's' : ''}
          </span>
          <span>Tipee : {sante.tipee_mode}</span>
          <span style={!sante.eveil.joignable ? { color: 'var(--err)' } : undefined}>
            Eveil V2.1 : {sante.eveil.joignable
              ? `${sante.eveil.personnes} personnes, ${sante.eveil.ateliers} ateliers${sante.eveil.lien_direct ? '' : ' (données locales de test)'}`
              : 'injoignable'}
          </span>
        </div>
      )}

      <div className="tabs">
        {ONGLETS.map((o) => (
          <div key={o.id} className={`tab ${onglet === o.id ? 'active' : ''}`} onClick={() => setOnglet(o.id)}>
            {o.label}
            {o.id === 'retours' && sante && sante.avis_nouveaux > 0 && (
              <span className="badge badge-warn" style={{ marginLeft: 6, padding: '1px 6px', fontSize: '0.75em', verticalAlign: 'middle' }}>
                {sante.avis_nouveaux}
              </span>
            )}
            {o.id === 'outbox' && sante && sante.outbox_en_attente > 0 && (
              <span className="badge badge-warn" style={{ marginLeft: 6, padding: '1px 6px', fontSize: '0.75em', verticalAlign: 'middle' }}>
                {sante.outbox_en_attente}
              </span>
            )}
          </div>
        ))}
      </div>

      {onglet === 'utilisateurs' && <UtilisateursOnglet />}
      {onglet === 'roles' && <RoleManagementPanel permissionKeys={[]} />}
      {onglet === 'reglages' && <ReglagesOnglet />}
      {onglet === 'coanimation' && <CoanimationOnglet />}
      {onglet === 'outbox' && <OutboxOnglet />}
      {onglet === 'eveil' && <EveilOnglet />}
      {onglet === 'audit' && <AuditLogViewer />}
      {onglet === 'notifications' && <NotificationPreferences />}
      {onglet === 'retours' && <RetoursOnglet onChange={() => api.getAdminStats().then(setSante).catch(() => {})} />}
    </div>
  );
}

function UtilisateursOnglet() {
  return (
    <>
      <div className="card">
        <p className="text-sm text-muted">
          Les rôles métier (intervenant, responsable, secrétariat, RH, planification) sont des
          rôles dynamiques du kit d'authentification. Ils s'assignent par utilisateur via le
          bouton « Roles » du tableau ci-dessous. Le rôle « admin » (accès à cette console) se
          gère séparément via « Make Admin ».
        </p>
      </div>
      <UserManagementPanel />
    </>
  );
}

const CHAMPS_DELAIS: { cle: string; label: string; aide?: string }[] = [
  {
    cle: 'recherche.relance_apres_heures', label: 'Relance Slack possible après (heures)',
    aide: "Après ce délai, l'intervenant·e peut relancer ses collègues et reçoit un rappel. Par défaut : 48.",
  },
  {
    cle: 'recherche.slack_round1_jours', label: 'Durée du 1er tour Slack (jours ouvrables)',
    aide: "Sans relance à la fin de ce tour, l'équipe gestion est prévenue.",
  },
  {
    cle: 'recherche.slack_round2_jours', label: 'Durée du 2e tour Slack, après relance (jours ouvrables)',
    aide: "À la fin de ce tour, l'équipe gestion prend le relais. Ne concerne pas les urgences.",
  },
  {
    cle: 'recherche.externe_jours', label: 'Réponse du vivier externe (jours)',
    aide: 'Délai laissé aux remplaçant·e·s externes pour répondre.',
  },
  {
    cle: 'remplacement.confirmation_jours', label: 'Réponse du/de la remplaçant·e désigné·e (jours ouvrables)',
    aide: "Délai laissé pour confirmer ou décliner. Un rappel part à mi-délai ; à l'échéance, la personne absente et l'équipe gestion sont alertées (rien ne change tout seul). Jamais après la veille de la séance. Par défaut : 3.",
  },
  {
    cle: 'tipee.battement_minutes', label: 'Absences Tipee — battement avant et après un atelier (minutes)',
    aide: "Écart minimal entre une absence et un atelier pour qu'aucune demande ne soit préparée. En dessous, une demande est préparée. Par défaut : 60.",
  },
  {
    cle: 'tipee.intervalle_minutes', label: 'Tipee — lecture toutes les (minutes)',
    aide: "Fréquence à laquelle le portail lit Tipee : nouvelles absences, et absences validées par les RH qui font passer une demande en passation. Par défaut : 10.",
  },
  {
    cle: 'tipee.relance_rh_jours', label: 'Tipee — rappel aux RH après (jours)',
    aide: "Si l'absence n'est toujours pas validée dans Tipee après ce délai, les RH et la personne absente reçoivent un rappel. Par défaut : 3.",
  },
  { cle: 'passation.logistique_preavis_jours', label: 'Passation — 1er rappel à la personne absente (jours avant la séance)' },
  { cle: 'passation.participants_preavis_jours', label: 'Passation — 2e rappel à la personne absente (jours avant la séance)' },
  { cle: 'urgence.relance_slack_minutes', label: 'Urgence — relance Slack (minutes)' },
  { cle: 'urgence.decision_management_minutes', label: 'Urgence — délai de décision management (minutes)' },
  { cle: 'fak.revue_mois', label: 'FRA — périodicité de revue (mois)' },
  { cle: 'vivier.revue_mois', label: 'Vivier externe — périodicité de revue (mois)' },
  { cle: 'calendrier.urgence_urgent_max_jours', label: 'Calendrier — seuil « Urgent » (jours avant la séance, max)' },
  { cle: 'calendrier.urgence_presse_max_jours', label: 'Calendrier — seuil « Pressé » (jours avant la séance, max)' },
  { cle: 'calendrier.urgence_alancer_max_jours', label: 'Calendrier — seuil « À lancer » (jours avant la séance, max)' },
];

function ReglagesOnglet() {
  const [valeurs, setValeurs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const [testingSlack, setTestingSlack] = useState(false);
  const [slackTestResult, setSlackTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testingChatbot, setTestingChatbot] = useState(false);
  const [chatbotTestResult, setChatbotTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    api.getSettings().then(setValeurs)
      .catch((err) => setError(err instanceof Error ? err.message : 'Échec du chargement'))
      .finally(() => setLoading(false));
  }, []);

  const set = (cle: string, valeur: string) => setValeurs((v) => ({ ...v, [cle]: valeur }));

  const flash = (marqueur: string) => {
    setSavedFlash(marqueur);
    setTimeout(() => setSavedFlash((c) => (c === marqueur ? null : c)), 1500);
  };

  const enregistrer = async (cle: string) => {
    setSaving(cle);
    setError(null);
    try {
      await api.updateSetting(cle, valeurs[cle] ?? '');
      if (cle === 'accent_color') document.documentElement.style.setProperty('--accent', valeurs[cle]);
      flash(cle);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'enregistrement");
    } finally {
      setSaving(null);
    }
  };

  const enregistrerGroupe = async (cles: string[]) => {
    const marqueur = cles.join(',');
    setSaving(marqueur);
    setError(null);
    try {
      await Promise.all(cles.map((c) => api.updateSetting(c, valeurs[c] ?? '')));
      flash(marqueur);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'enregistrement");
    } finally {
      setSaving(null);
    }
  };

  const testerSlack = async () => {
    setTestingSlack(true);
    setSlackTestResult(null);
    try {
      await api.testSlack();
      setSlackTestResult({ ok: true, message: 'Message envoyé' });
    } catch (err) {
      setSlackTestResult({ ok: false, message: err instanceof Error ? err.message : 'Échec du test' });
    } finally {
      setTestingSlack(false);
    }
  };

  const testerChatbot = async () => {
    setTestingChatbot(true);
    setChatbotTestResult(null);
    try {
      const { reponse } = await api.testChatbot();
      setChatbotTestResult({ ok: true, message: reponse });
    } catch (err) {
      setChatbotTestResult({ ok: false, message: err instanceof Error ? err.message : 'Échec du test' });
    } finally {
      setTestingChatbot(false);
    }
  };

  if (loading) return <div className="spinner" />;

  const clesDelais = CHAMPS_DELAIS.map((c) => c.cle);
  const clesTipee = ['tipee.mode', 'tipee.instance_url', 'tipee.api_key'];
  const clesChatbot = ['chatbot.mode', 'chatbot.base_url', 'chatbot.model', 'chatbot.api_key', 'chatbot.reflexion', 'chatbot.max_tokens'];

  return (
    <div>
      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}

      <div className="card">
        <h3 className="card-title">Apparence</h3>
        <div className="form-group">
          <label className="form-label">Couleur d'accent</label>
          <div className="flex gap-8 items-center">
            <input type="color" value={valeurs.accent_color ?? '#2563eb'}
              onChange={(e) => { set('accent_color', e.target.value); document.documentElement.style.setProperty('--accent', e.target.value); }}
              style={{ width: 44, height: 32, padding: 2 }} />
            <input className="form-input" style={{ maxWidth: 140 }} value={valeurs.accent_color ?? ''} onChange={(e) => set('accent_color', e.target.value)} />
            <button className="btn btn-accent btn-sm" onClick={() => enregistrer('accent_color')} disabled={saving === 'accent_color'}>Enregistrer</button>
            {savedFlash === 'accent_color' && <span className="text-ok text-sm">Enregistré</span>}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">Délais &amp; seuils</h3>
        {CHAMPS_DELAIS.map((c) => (
          <div className="form-group" key={c.cle}>
            <label className="form-label">{c.label}</label>
            {c.aide && <div className="text-xs text-ink-3 mb-4">{c.aide}</div>}
            <input type="number" min={0} className="form-input" style={{ maxWidth: 120 }} value={valeurs[c.cle] ?? ''} onChange={(e) => set(c.cle, e.target.value)} />
          </div>
        ))}
        <button className="btn btn-accent" onClick={() => enregistrerGroupe(clesDelais)} disabled={saving === clesDelais.join(',')}>
          Enregistrer les délais
        </button>
        {savedFlash === clesDelais.join(',') && <span className="text-ok text-sm ml-8">Enregistré</span>}
      </div>

      <div className="card">
        <h3 className="card-title">Tableau de bord — Demandes des collègues</h3>
        <p className="text-sm text-muted mb-8">
          Quelles demandes de remplacement en recherche voit un·e intervenant·e dans l'onglet « Demandes des
          collègues » (lecture limitée : ni motif, ni notes, ni historique). Les comptes externes n'y ont jamais accès.
        </p>
        <div className="form-group">
          <label className="form-label">Périmètre</label>
          <select className="form-select" value={valeurs['tableau.scope_collegues'] ?? 'tous'} onChange={(e) => set('tableau.scope_collegues', e.target.value)}>
            <option value="tous">Toutes les demandes, vos pôles en premier</option>
            <option value="pole">Même pôle uniquement</option>
            <option value="pole_et_historique">Même pôle + personnes déjà remplacées</option>
            <option value="off">Désactivé (onglet masqué)</option>
          </select>
        </div>
        <button className="btn btn-accent" onClick={() => enregistrer('tableau.scope_collegues')} disabled={saving === 'tableau.scope_collegues'}>
          Enregistrer
        </button>
        {savedFlash === 'tableau.scope_collegues' && <span className="text-ok text-sm ml-8">Enregistré</span>}
      </div>

      <div className="card">
        <h3 className="card-title">Intégration Tipee</h3>
        <div className="form-group">
          <label className="form-label">Mode</label>
          <select className="form-select" value={valeurs['tipee.mode'] ?? 'stub'} onChange={(e) => set('tipee.mode', e.target.value)}>
            <option value="stub">Stub (données factices)</option>
            <option value="live">Live (API Tipee)</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">URL de l'instance</label>
          <div className="text-xs text-ink-3 mb-4">Même adresse pour l'API et pour le lien « Ouvrir Tipee » (https://aeveil.tipee.net).</div>
          <input className="form-input" value={valeurs['tipee.instance_url'] ?? ''} onChange={(e) => set('tipee.instance_url', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Clé API</label>
          <input type="password" className="form-input" value={valeurs['tipee.api_key'] ?? ''} onChange={(e) => set('tipee.api_key', e.target.value)} />
        </div>
        <button className="btn btn-accent" onClick={() => enregistrerGroupe(clesTipee)} disabled={saving === clesTipee.join(',')}>
          Enregistrer Tipee
        </button>
        {savedFlash === clesTipee.join(',') && <span className="text-ok text-sm ml-8">Enregistré</span>}
      </div>

      <div className="card">
        <h3 className="card-title">Slack</h3>
        <div className="form-group">
          <label className="form-label">Webhook URL</label>
          <input className="form-input" value={valeurs['slack.webhook_url'] ?? ''} onChange={(e) => set('slack.webhook_url', e.target.value)} />
        </div>
        <div className="flex gap-8 items-center flex-wrap">
          <button className="btn btn-accent" onClick={() => enregistrer('slack.webhook_url')} disabled={saving === 'slack.webhook_url'}>
            Enregistrer Slack
          </button>
          <button className="btn" onClick={testerSlack} disabled={testingSlack || !valeurs['slack.webhook_url']}>
            {testingSlack ? 'Envoi…' : 'Tester'}
          </button>
          {savedFlash === 'slack.webhook_url' && <span className="text-ok text-sm">Enregistré</span>}
          {slackTestResult && (
            <span className={`text-sm ${slackTestResult.ok ? 'text-ok' : 'text-err'}`}>
              {slackTestResult.message}
            </span>
          )}
        </div>
      </div>

      <CopieSeafileCarte valeurs={valeurs} set={set} enregistrerGroupe={enregistrerGroupe} saving={saving} savedFlash={savedFlash} />

      <div className="card">
        <h3 className="card-title">Assistant (endpoint compatible OpenAI)</h3>
        <p className="text-sm text-muted mb-8">
          Bulle d'aide flottante visible par tous les utilisateurs. Niveau 1 : aide statique sur
          le fonctionnement de l'app, aucune donnée réelle transmise. Parle le protocole standard
          « chat completions » compatible OpenAI — n'importe quel service qui l'implémente peut être
          branché ici (par défaut : Claude, via la couche de compatibilité OpenAI d'Anthropic).
        </p>
        <div className="form-group">
          <label className="form-label">Mode</label>
          <select className="form-select" value={valeurs['chatbot.mode'] ?? 'stub'} onChange={(e) => set('chatbot.mode', e.target.value)}>
            <option value="stub">Stub (réponse factice, sans clé API)</option>
            <option value="live">Live (endpoint compatible OpenAI)</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">URL de base</label>
          <input
            className="form-input"
            placeholder="https://api.anthropic.com/v1/"
            value={valeurs['chatbot.base_url'] ?? ''}
            onChange={(e) => set('chatbot.base_url', e.target.value)}
          />
          <div className="text-xs text-ink-3 mt-4">
            Vide = Claude par défaut (via la compatibilité OpenAI d'Anthropic). Remplacer par l'URL
            de votre IA interne le moment venu.
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Modèle</label>
          <input className="form-input" placeholder="claude-sonnet-5" value={valeurs['chatbot.model'] ?? ''} onChange={(e) => set('chatbot.model', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Clé API</label>
          <input type="password" className="form-input" value={valeurs['chatbot.api_key'] ?? ''} onChange={(e) => set('chatbot.api_key', e.target.value)} />
          <div className="text-xs text-ink-3 mt-4">Proxy LiteLLM interne : utilisez sa clé maître.</div>
        </div>
        <div className="flex gap-12">
          <div className="form-group flex-1">
            <label className="form-label">Réflexion du modèle</label>
            <select className="form-select" value={valeurs['chatbot.reflexion'] ?? 'faible'} onChange={(e) => set('chatbot.reflexion', e.target.value)}>
              <option value="faible">Faible — réponses en quelques secondes (recommandé)</option>
              <option value="desactivee">Désactivée (modèles Qwen)</option>
              <option value="normale">Normale — plus lent ; à choisir si le service refuse les autres</option>
            </select>
          </div>
          <div className="form-group flex-1">
            <label className="form-label">Longueur maximale d'une réponse (jetons)</label>
            <input type="number" min={256} className="form-input" value={valeurs['chatbot.max_tokens'] ?? '16000'} onChange={(e) => set('chatbot.max_tokens', e.target.value)} />
          </div>
        </div>
        <div className="flex gap-8 items-center flex-wrap">
          <button className="btn btn-accent" onClick={() => enregistrerGroupe(clesChatbot)} disabled={saving === clesChatbot.join(',')}>
            Enregistrer Assistant
          </button>
          <button className="btn" onClick={testerChatbot} disabled={testingChatbot}>
            {testingChatbot ? 'Envoi…' : 'Tester'}
          </button>
          {savedFlash === clesChatbot.join(',') && <span className="text-ok text-sm">Enregistré</span>}
          {chatbotTestResult && (
            <span className={`text-sm ${chatbotTestResult.ok ? 'text-ok' : 'text-err'}`}>
              {chatbotTestResult.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Copie Excel du registre dans Seafile (DECISIONS §35) : état, réglages de prod, envoi immédiat.
const CLES_SEAFILE_PROD = ['seafile.url', 'seafile.jeton', 'seafile.dossier', 'export_excel.intervalle_minutes'];
const CLES_SEAFILE_DEV = ['export_excel.intervalle_minutes'];

function dateHeure(iso: string): string {
  return new Date(iso).toLocaleString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function CopieSeafileCarte({ valeurs, set, enregistrerGroupe, saving, savedFlash }: {
  valeurs: Record<string, string>;
  set: (cle: string, valeur: string) => void;
  enregistrerGroupe: (cles: string[]) => Promise<void>;
  saving: string | null;
  savedFlash: string | null;
}) {
  const [etat, setEtat] = useState<EtatExportExcel | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState<{ ok: boolean; message: string } | null>(null);

  const charger = useCallback(() => {
    api.getEtatExportExcel().then(setEtat).catch(() => setEtat(null));
  }, []);
  useEffect(charger, [charger]);

  const cles = etat?.dev ? CLES_SEAFILE_DEV : CLES_SEAFILE_PROD;
  const marqueur = cles.join(',');

  const enregistrer = async () => {
    await enregistrerGroupe(cles);
    charger();
  };

  const envoyer = async () => {
    setEnvoi(true);
    setResultat(null);
    try {
      const r = await api.envoyerExportExcel();
      setResultat({ ok: true, message: `Déposé dans « ${r.bibliotheque} » : ${r.envoyes.join(', ')}` });
    } catch (err) {
      setResultat({ ok: false, message: err instanceof Error ? err.message : "Échec de l'envoi" });
    } finally {
      setEnvoi(false);
      charger();
    }
  };

  return (
    <div className="card">
      <h3 className="card-title">Copie Excel dans Seafile</h3>
      <p className="text-sm text-muted mb-8">
        TacTac dépose le registre des absences et remplacements, « Absences Intervenants Eveil (année).xlsx » (une feuille
        par mois, les colonnes de l'ancien classeur, en orange ce qui reste à traiter), dans une bibliothèque Seafile que
        l'équipe peut lire sans la modifier. Le fichier est remplacé dès que les données changent.
      </p>

      {etat && (
        <div className="text-sm mb-8">
          {!etat.configure && <span className="text-muted">Pas configurée : aucun fichier n'est envoyé.</span>}
          {etat.configure && etat.erreur_bibliotheque && <span className="text-err">{etat.erreur_bibliotheque}</span>}
          {etat.configure && etat.bibliotheque && (
            <span>
              Bibliothèque « {etat.bibliotheque} », dossier {etat.dossier}
              {etat.dernier_envoi ? ` · dernier envoi le ${dateHeure(etat.dernier_envoi)}` : ' · encore rien envoyé'}
            </span>
          )}
          {etat.derniere_erreur && <div className="text-err">Dernière erreur : {etat.derniere_erreur}</div>}
        </div>
      )}

      {etat?.dev ? (
        <p className="text-sm text-muted mb-8">
          Sur dev, la copie part dans une bibliothèque de test non partagée (SEAFILE_DEV_URL et SEAFILE_DEV_JETON dans
          Coolify) : les réglages Seafile de la production ne s'appliquent pas ici.
        </p>
      ) : (
        <>
          <div className="form-group">
            <label className="form-label" htmlFor="seafile-url">Adresse Seafile</label>
            <input id="seafile-url" className="form-input" placeholder="https://documents.sintra.ch"
              value={valeurs['seafile.url'] ?? ''} onChange={(e) => set('seafile.url', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="seafile-jeton">Jeton de la bibliothèque (API token, lecture-écriture)</label>
            <input id="seafile-jeton" className="form-input" type="password" autoComplete="off"
              value={valeurs['seafile.jeton'] ?? ''} onChange={(e) => set('seafile.jeton', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="seafile-dossier">Dossier dans la bibliothèque</label>
            <input id="seafile-dossier" className="form-input" placeholder="/"
              value={valeurs['seafile.dossier'] ?? ''} onChange={(e) => set('seafile.dossier', e.target.value)} />
          </div>
        </>
      )}
      <div className="form-group">
        <label className="form-label" htmlFor="export-intervalle">Vérifier les changements toutes les … minutes</label>
        <input id="export-intervalle" className="form-input" type="number" min={1} style={{ maxWidth: 120 }}
          value={valeurs['export_excel.intervalle_minutes'] ?? '10'} onChange={(e) => set('export_excel.intervalle_minutes', e.target.value)} />
      </div>

      <div className="flex gap-8 items-center flex-wrap">
        <button className="btn btn-accent" onClick={enregistrer} disabled={saving === marqueur}>
          Enregistrer
        </button>
        <button className="btn" onClick={envoyer} disabled={envoi || !etat?.configure}>
          {envoi ? 'Envoi…' : 'Envoyer maintenant'}
        </button>
        {savedFlash === marqueur && <span className="text-ok text-sm">Enregistré</span>}
        {resultat && <span className={`text-sm ${resultat.ok ? 'text-ok' : 'text-err'}`}>{resultat.message}</span>}
      </div>
    </div>
  );
}

function OutboxOnglet() {
  const [messages, setMessages] = useState<OutboxMessage[]>([]);
  const [statutFiltre, setStatutFiltre] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getOutbox(statutFiltre ? { statut: statutFiltre } : {})
      .then(setMessages)
      .catch((err) => setError(err instanceof Error ? err.message : 'Échec du chargement'))
      .finally(() => setLoading(false));
  }, [statutFiltre]);

  useEffect(load, [load]);

  const copier = async (m: OutboxMessage) => {
    const texte = `${m.sujet ? m.sujet + '\n\n' : ''}${m.corps}`;
    try {
      await navigator.clipboard.writeText(texte);
      setCopiedId(m.id);
      setTimeout(() => setCopiedId((c) => (c === m.id ? null : c)), 1500);
    } catch {
      setError('Impossible de copier dans le presse-papiers');
    }
  };

  const marquer = async (id: number) => {
    try {
      const updated = await api.marquerEnvoye(id);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updated } : m)));
      annoncerMiseAJourMessages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la mise à jour');
    }
  };

  return (
    <div className="card">
      <div className="toolbar">
        <div className="filter-chips">
          {(['', 'a_envoyer', 'envoi_en_cours', 'envoye', 'erreur', 'plus_utile'] as const).map((s) => (
            <button key={s || 'tous'} type="button" className={`chip ${statutFiltre === s ? 'active' : ''}`} onClick={() => setStatutFiltre(s)}>
              {s === '' ? 'Tous' : LIBELLE_ETAT[s as StatutMessage]}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}
      {loading ? (
        <div className="spinner" />
      ) : messages.length === 0 ? (
        <div className="empty-state">Aucun message pour l'instant.</div>
      ) : (
        <table className="table">
          <thead><tr><th>Canal</th><th>Destinataire</th><th>Sujet / corps</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {messages.map((m) => (
              <tr key={m.id}>
                <td><span className="badge badge-accent">{LIBELLE_CANAL[m.canal]}</span></td>
                <td className="text-sm">{m.destinataire ?? '—'}{m.atelier_nom && <div className="text-xs text-ink-3">{m.atelier_nom}</div>}</td>
                <td className="text-sm" style={{ maxWidth: 420 }}>
                  {m.sujet && <div className="font-medium">{m.sujet}</div>}
                  <div className={expandedId === m.id ? '' : 'truncate'} style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}>
                    {m.corps}
                  </div>
                  <button className="btn btn-sm mt-4" onClick={() => copier(m)}>
                    <Copy className="icon-sm" /> {copiedId === m.id ? 'Copié' : 'Copier'}
                  </button>
                </td>
                <td>
                  <span className={CLASSE_BADGE_ETAT[m.statut]}>{LIBELLE_ETAT[m.statut]}</span>
                  <div className="text-xs text-ink-3 mt-4">{ligneEtat(m, true)}</div>
                </td>
                <td>
                  {(m.statut === 'a_envoyer' || m.statut === 'erreur') && (
                    <button className="btn btn-sm" onClick={() => marquer(m.id)}>Marquer comme envoyé</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Ateliers à deux animateur·rice·s (DECISIONS §36). V2.1 donne deux personnes à
 * ces créneaux sans dire si elles animent ensemble ou à tour de rôle : la
 * gestion le dit ici, en attendant que la base centrale le sache.
 */
function CoanimationOnglet() {
  const [ateliers, setAteliers] = useState<AtelierCoanimation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<number | null>(null);
  const [enregistre, setEnregistre] = useState<number | null>(null);

  useEffect(() => {
    api.getAteliersCoanimation()
      .then(setAteliers)
      .catch((err) => setError(err instanceof Error ? err.message : 'Chargement impossible'))
      .finally(() => setLoading(false));
  }, []);

  const changer = async (a: AtelierCoanimation, valeur: string) => {
    const mode = (valeur === '' ? null : valeur) as ModeCoanimation | null;
    setEnCours(a.id);
    setError(null);
    try {
      await api.updateAtelier(a.id, { mode_coanimation: mode });
      setAteliers((liste) => liste.map((x) => (x.id === a.id ? { ...x, mode_coanimation: mode } : x)));
      setEnregistre(a.id);
      setTimeout(() => setEnregistre((id) => (id === a.id ? null : id)), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'enregistrement");
    } finally {
      setEnCours(null);
    }
  };

  const aDefinir = ateliers.filter((a) => a.mode_coanimation == null).length;

  return (
    <div className="card">
      <h3 className="card-title">Ateliers à deux animateur·rice·s</h3>
      <p className="text-sm text-muted mb-8">
        Ces ateliers ont deux personnes dans Eveil, mais rien n'y dit si elles animent ensemble ou à tour de rôle.
        Choisissez le mode de chacun : TacTac en tient compte quand une absence est repérée dans Tipee, et l'indique sur
        les demandes.
      </p>
      <ul className="text-sm text-muted mb-12" style={{ paddingLeft: 18 }}>
        <li><strong>{LIBELLE_MODE_COANIMATION.binome}</strong> : {EXPLICATION_MODE_COANIMATION.binome}</li>
        <li><strong>{LIBELLE_MODE_COANIMATION.alternance}</strong> : {EXPLICATION_MODE_COANIMATION.alternance} La demande reste
          réservée à la gestion tant qu'elle n'est pas envoyée.</li>
        <li><strong>À définir</strong> : comme avant, seule l'absence de la première personne prépare une demande.</li>
      </ul>
      {loading ? (
        <div className="text-sm text-muted">Chargement…</div>
      ) : ateliers.length === 0 ? (
        <div className="empty-state">Aucun atelier actif n'a deux animateur·rice·s dans Eveil.</div>
      ) : (
        <>
          {aDefinir > 0 && (
            <p className="text-sm mb-8" style={{ color: 'var(--warn)' }}>
              {aDefinir} atelier{aDefinir > 1 ? 's' : ''} à définir.
            </p>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Atelier</th><th>Animateur·rice·s</th><th>Mode</th><th>Demandes à venir</th></tr></thead>
              <tbody>
                {ateliers.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <a href={`#ateliers?atelier=${a.id}`} className="font-medium">{a.nom}</a>
                      <div className="text-xs text-ink-3">
                        {a.jour_semaine ? JOURS_SEMAINE[a.jour_semaine] : '—'} {formatHoraire(a.heure_debut, a.heure_fin)}
                        {(a.interval_weeks ?? 1) > 1 ? ` · une semaine sur ${a.interval_weeks}` : ''}
                        {a.lieu ? ` · ${a.lieu}` : ''}
                      </div>
                    </td>
                    <td className="text-sm">
                      {a.titulaire_nom ?? '—'}
                      <div>{a.co_animateur_nom ?? '—'}</div>
                    </td>
                    <td>
                      <div className="flex gap-8 items-center">
                        <select className="form-select" value={a.mode_coanimation ?? ''} disabled={enCours === a.id}
                          aria-label={`Mode de co-animation : ${a.nom}`}
                          onChange={(e) => changer(a, e.target.value)}>
                          <option value="">À définir</option>
                          <option value="binome">{LIBELLE_MODE_COANIMATION.binome}</option>
                          <option value="alternance">{LIBELLE_MODE_COANIMATION.alternance}</option>
                        </select>
                        {enregistre === a.id && <span className="text-ok text-sm">Enregistré</span>}
                      </div>
                    </td>
                    <td className="text-sm tabular-nums">
                      {a.demandes_ouvertes > 0 ? a.demandes_ouvertes : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {error && <div className="badge badge-err mt-12" style={{ display: 'block' }}>{error}</div>}
    </div>
  );
}

function EveilOnglet() {
  const [rapport, setRapport] = useState<Record<string, number> | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [perimetre, setPerimetre] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getSettings().then((s) => setPerimetre(s['perimetre.categories'] ?? 'Atelier,Espace')).catch(() => {});
  }, []);

  const lancer = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await api.syncEveil();
      setRapport(res.rapport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la synchronisation');
    } finally {
      setRunning(false);
    }
  };

  const enregistrerPerimetre = async () => {
    setError(null);
    try {
      await api.updateSetting('perimetre.categories', perimetre.split(',').map((c) => c.trim()).filter(Boolean).join(','));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'enregistrement");
    }
  };

  const LIBELLES: Record<string, string> = {
    internes: 'Collaborateur·rice·s ajouté·e·s',
    externes: 'Externes ajouté·e·s',
    candidats_rattaches: 'Candidat·e·s du vivier rattaché·e·s à leur fiche Eveil',
    stagiaires: 'Stagiaires ajouté·e·s',
    seances_completees: 'Demandes reliées à leur séance',
  };

  return (
    <>
      <div className="card">
        <h3 className="card-title">Données d'Eveil V2.1</h3>
        <p className="text-sm text-muted mb-12">
          Personnes, ateliers, pôles, séances et participant·e·s sont lus en direct dans Eveil V2.1 :
          ils se corrigent dans le CRM, pas ici. Le portail ne garde que ses propres données
          (demandes, vivier, réglages des ateliers). La synchronisation ci-dessous ajoute les
          nouvelles personnes à l'annuaire du portail ; elle tourne aussi toute seule toutes les 15 minutes.
        </p>
        <button className="btn btn-accent" onClick={lancer} disabled={running}>
          <RefreshCw className="icon-sm" /> {running ? 'Synchronisation…' : 'Synchroniser maintenant'}
        </button>
        {rapport && (
          <dl className="mt-16" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
            {Object.entries(rapport).map(([k, v]) => (
              <Fragment key={k}>
                <dt className="text-sm font-medium">{LIBELLES[k] ?? k}</dt>
                <dd className="text-sm text-muted" style={{ margin: 0 }}>{v}</dd>
              </Fragment>
            ))}
          </dl>
        )}
      </div>
      <div className="card">
        <h3 className="card-title">Périmètre des remplacements</h3>
        <p className="text-sm text-muted mb-8">
          Catégories Eveil dont les séances se remplacent, séparées par des virgules
          (Atelier, Espace, Suivi individuel, Placement).
        </p>
        <div className="flex gap-8 items-center">
          <input className="form-input" style={{ maxWidth: 320 }} value={perimetre} onChange={(e) => setPerimetre(e.target.value)} />
          <button className="btn btn-accent" onClick={enregistrerPerimetre}>Enregistrer</button>
          {saved && <span className="text-ok text-sm">Enregistré</span>}
        </div>
      </div>
      {error && <div className="badge badge-err mt-12" style={{ display: 'block' }}>{error}</div>}
    </>
  );
}

const LIBELLE_TYPE_AVIS = { probleme: 'Quelque chose ne marche pas', idee: "Une idée d'amélioration", comprends: 'Je ne comprends pas', autre: 'Autre' } as const;
const LIBELLE_STATUT_AVIS: Record<StatutAvis, string> = { nouveau: 'Nouveau', vu: 'Vu', fait: 'Fait' };
const CLASSE_STATUT_AVIS: Record<StatutAvis, string> = { nouveau: 'badge badge-warn', vu: 'badge badge-accent', fait: 'badge badge-ok' };

/** Les avis écrits depuis « Votre avis » (DECISIONS §32) : qui, où, quoi, et l'état du traitement. */
function RetoursOnglet({ onChange }: { onChange: () => void }) {
  const [retours, setRetours] = useState<RetourAvis[]>([]);
  const [filtre, setFiltre] = useState<StatutAvis | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getAvis(filtre || undefined)
      .then(setRetours)
      .catch((err) => setError(err instanceof Error ? err.message : 'Échec du chargement'))
      .finally(() => setLoading(false));
  }, [filtre]);
  useEffect(load, [load]);

  const changer = async (id: number, statut: StatutAvis) => {
    try {
      await api.changerStatutAvis(id, statut);
      setRetours((prev) => prev.map((r) => (r.id === id ? { ...r, statut } : r)));
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la mise à jour');
    }
  };

  return (
    <div className="card">
      <p className="text-sm text-ink-2 mb-12">
        Chaque avis écrit depuis « Votre avis » arrive ici. « Ouvrir » rouvre la page dans l'état où la personne se trouvait. Quand vous passez un avis à « Fait »,
        son auteur·e en est prévenu·e dans la cloche (si elle l'a demandé).
      </p>
      <div className="toolbar">
        <div className="filter-chips">
          {(['', 'nouveau', 'vu', 'fait'] as const).map((s) => (
            <button key={s || 'tous'} type="button" className={`chip ${filtre === s ? 'active' : ''}`} onClick={() => setFiltre(s)}>
              {s === '' ? 'Tous' : LIBELLE_STATUT_AVIS[s]}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}
      {loading ? (
        <div className="spinner" />
      ) : retours.length === 0 ? (
        <div className="empty-state">{filtre ? 'Aucun avis dans cet état.' : "Aucun avis pour l'instant. Le bouton « Votre avis » est en bas du menu de gauche."}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead><tr><th>Quand</th><th>Qui</th><th>Où</th><th>Message</th><th>État</th></tr></thead>
            <tbody>
              {retours.map((r) => (
                <tr key={r.id}>
                  <td className="text-sm" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(r.created_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' })}
                    <div className="text-xs text-ink-3">{new Date(r.created_at).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}</div>
                  </td>
                  <td className="text-sm">{r.auteur_nom}{r.auteur_roles && <div className="text-xs text-ink-3">{r.auteur_roles}</div>}</td>
                  <td className="text-sm" style={{ maxWidth: 260 }}>
                    {r.element ? r.element.libelle : <em>Page entière</em>}
                    <div className="text-xs text-ink-3">
                      {r.page} · <a href={r.lien}>Ouvrir</a>
                    </div>
                    {r.fenetre && <div className="text-xs text-ink-3">Fenêtre {r.fenetre}</div>}
                  </td>
                  <td className="text-sm" style={{ maxWidth: 420 }}>
                    {r.type && <div className="font-medium">{LIBELLE_TYPE_AVIS[r.type]}</div>}
                    <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.message}</div>
                  </td>
                  <td>
                    <span className={CLASSE_STATUT_AVIS[r.statut]}>{LIBELLE_STATUT_AVIS[r.statut]}</span>
                    <select className="form-select mt-4" aria-label={`État de l'avis ${dePersonne(r.auteur_nom)}`} value={r.statut} onChange={(e) => changer(r.id, e.target.value as StatutAvis)}>
                      {(['nouveau', 'vu', 'fait'] as const).map((s) => <option key={s} value={s}>{LIBELLE_STATUT_AVIS[s]}</option>)}
                    </select>
                    {r.traite_par_nom && r.statut !== 'nouveau' && <div className="text-xs text-ink-3 mt-4">par {r.traite_par_nom}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
